import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AlertIcons } from '../../../../models/api/alerts/alerticons';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { ClaimSummaryInfo } from '../../../../models/api/view/claim/summary/claimsummaryinfo';
import { BaseComponent } from '../../../base/base.component';
import { MoreButtonComponent } from '../../../core/buttons/more-button.component';
import { AlertLabelComponent } from '../../../core/labels/alert-label.component';
import { ClaimLabelComponent } from '../../../core/labels/claim-label.component';
import { TagComponent, TagType } from '../../../core/labels/tag.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { getFieldVisibility } from '../../../../functions/field-security';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-ds-claim-card',
  imports: [
    CommonModule,
    DetailPanelComponent,
    DetailItemComponent,
    TagComponent,
    ActionRowComponent,
    MoreButtonComponent,
    AlertLabelComponent,
    LoadingPanelComponent,
    ClaimLabelComponent,
  ],
  template: `
    <app-loading-panel
      [state]="loadingState"
      [loadingHeight]="300"
      loadingStyle="Box"
      [error]="error"
    >
      <ng-template #loaded>
        <div class="tw-w-full">
          <div
            class="tw-flex tw-flex-col md:tw-flex-row tw-gap-0 md:tw-gap-8 tw-w-full"
          >
            <app-detail-panel
              [dataSource]="claimSummaryInfo"
              class="tw-w-full "
              [showEmpty]="false"
              showEmptySource="item"
            >
              <app-detail-item label="Claim Number" name="fileNumber">
                <ng-template let-data #value>
                  <app-claim-label [claim]="claimRow" [readOnly]="true" />
                </ng-template>
              </app-detail-item>
              <app-detail-item label="Status">
                <ng-template let-data #value>
                  <app-tag [type]="getClaimStatusLabel(data.claimStatus)">{{
                    getClaimStatusValue(
                      data.claimStatus,
                      data.claimStatus !== 'Incident'
                        ? data.claimSubStatus
                        : '',
                      data.claimSubReason
                    )
                  }}</app-tag>
                </ng-template>
              </app-detail-item>

              <app-detail-item label="Type" name="type" />
              @if (showSubType) {
                <app-detail-item
                  label="Subtype"
                  name="subType"
                  [showEmpty]="false"
                />
              }
              @if (claimRow.coverageCode !== 'AC') {
                <app-detail-item
                  label="Disability Due to"
                  name="disablityDueTo"
                />
                @if (showBenefitPlanId) {
                  <app-detail-item
                    label="Benefit plan ID"
                    name="benefitPlanID"
                    labelCase="none"
                  />
                }
              }
            </app-detail-panel>

            <app-detail-panel
              [dataSource]="claimSummaryInfo"
              class="tw-w-full "
              [showEmpty]="false"
              showEmptySource="item"
            >
              <app-detail-item
                label="Date of Loss"
                name="lossDate"
                format="d"
              />

              <!-- new fields -->

              @if (showDateOpened) {
                <app-detail-item label="Opened" name="dateOpened" format="d" />
              }
              @if (showDateClosed) {
                <app-detail-item label="Closed" name="dateClosed" format="d" />
              }
              @if (showDateRptByClient) {
                <app-detail-item
                  label="Reported"
                  name="dateRptByClient"
                  format="d"
                />
              }
              @if (showDateReptToTPA) {
                <app-detail-item
                  label="Reported to Sedgwick"
                  labelCase="none"
                  name="dateReptToTPA"
                  format="d"
                />
              }
            </app-detail-panel>
          </div>

          @if (
            this.restDsLaAuth &&
            this.custDispAuth &&
            this.showClaimDescription &&
            claimSummaryInfo?.claimDescription
          ) {
            <div>
              <div class="tw-font-bold tw-py-2">Event description</div>
              <div class="tw-pb-4 tw-whitespace-pre-line">
                {{ claimSummaryInfo.claimDescription }}
              </div>
            </div>
          }

          <app-action-row>
            <div class="tw-flex tw-flex-wrap tw-gap-4 tw-max-w-[420px]" left1>
              @for (alert of alerts; track alert) {
                @if (getAlertVisibility(alert)) {
                  <app-alert-label
                    [icon]="getAlertIcon(alert)"
                    [label]="getAlertLabel(alert)"
                    [title]="getAlertTitle(alert)"
                  ></app-alert-label>
                }
              }
            </div>
            @if (this.disabilityAuth) {
              <app-more-button
                right1
                (click)="this.view.navigateCurrentClaim(['disability'])"
                [ariaLabel]="
                  'Navigate to disability for ' + claimRow?.fileNumber
                "
              />
            }
          </app-action-row>
        </div>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class DsClaimCardComponent extends BaseComponent implements OnInit {
  claimRow: ClaimRow;
  claimSummaryInfo: ClaimSummaryInfo;
  alerticons: AlertIcons;
  alerts = [
    'Litigation',
    'Relapse',
    'Subrogation',
    'ERISA',
    'MidRange',
    'At-risk',
    'Sub Owner',
    'OverPaid-Balance',
    'OverPaid-Recovered',
    'Tendered',
    'Triage',
    'Claimant Represented',
    'Alert',
    'Work related',
  ];

  litigation = false;
  relapse = false;
  subro = false;
  appeal = false;
  midRange = false;
  atRisk = false;
  subOwner = false;
  overpayBalance = false;
  overpayRecovered = false;
  tendered = false;
  nurseTriage = false;
  alertTextEvent = '';
  alertTextClaim = '';
  alertTextClaimant = '';
  claimantRepresented = false;
  workrelated = false;
  restDsLaAuth = false;
  custDispAuth = false;
  disabilityAuth = false;

  /** Field visibility from field security (set once in ngOnInit to avoid template re-evaluation). */
  showSubType = true;
  showDateOpened = true;
  showDateClosed = true;
  showDateRptByClient = true;
  showDateReptToTPA = true;
  showClaimDescription = true;
  showBenefitPlanId = true;

  loadingState: LoadingState = 'Loading';
  error: any = null;

  trackAlerticon(index: number, alerticon: string): string {
    return alerticon;
  }

  getAlertVisibility(alerticon: string): boolean {
    switch (alerticon) {
      case 'Litigation':
        return this.litigation;
      case 'Relapse':
        return this.relapse;
      case 'Subrogation':
        return this.subro;
      case 'ERISA':
        return this.appeal;
      case 'MidRange':
        return this.midRange;
      case 'At-risk':
        return this.atRisk;
      case 'Sub Owner':
        return this.subOwner;
      case 'OverPaid-Balance':
        return this.overpayBalance;
      case 'OverPaid-Recovered':
        return this.overpayRecovered;
      case 'Tendered':
        return this.tendered;
      case 'Triage':
        return this.nurseTriage;
      case 'Claimant Represented':
        return this.claimantRepresented;
      case 'Alert':
        return (
          this.alertTextEvent !== '' ||
          this.alertTextClaim !== '' ||
          this.alertTextClaimant !== ''
        );
      case 'Work related':
        return this.workrelated;
      default:
        return false;
    }
  }
  getClaimStatusLabel(Status: string): TagType {
    switch (Status) {
      case 'Open':
      case 'Reopen':
        return 'Success';
      case 'Closed':
        return 'Error';
      case 'Incident':
        return 'Warn';
      default:
        return 'Success';
    }
  }

  getClaimStatusValue(
    Status: string,
    SubStatus?: string,
    SubReason?: string,
  ): string {
    let result = Status || '';
    if (SubStatus) {
      result += ' - ' + SubStatus;
    }
    if (SubReason) {
      result += ' - ' + SubReason;
    }
    return result;
  }

  getAlertIcon(alerticon: string): string {
    switch (alerticon) {
      case 'Litigation':
        return 'fa-solid fa-gavel';
      case 'Relapse':
        return 'fa-solid fa-repeat';
      case 'Subrogation':
        return 'fa-solid fa-hand-point-left';
      case 'ERISA':
        return 'fa-light fa fa-briefcase';
      case 'MidRange':
        return 'fa-solid fa-person-circle-exclamation';
      case 'At-risk':
        return 'fa-solid fa-person-circle-exclamation';
      case 'Sub Owner':
        return 'fa-solid fa-people-group';
      case 'OverPaid-Balance':
        return 'fa-solid fa-sack-dollar';
      case 'OverPaid-Recovered':
        return 'fa-solid fa-receipt';
      case 'Tendered':
        return 'fa-solid fa-file-invoice-dollar';
      case 'Triage':
        return 'fa-solid fa-user-nurse';
      case 'Claimant Represented':
        return 'fa-solid fa-user-group';
      case 'Alert':
        return 'fa-solid fa-message-exclamation';
      case 'Work related':
        return 'fa-solid fa-helmet-safety';
      default:
        return '';
    }
  }

  getAlertLabel(alerticon: string): string {
    if (alerticon === 'ERISA') return 'ERISA';
    if (alerticon === 'Alert') return 'Alert';
    return alerticon;
  }

  getAlertTitle(alerticon: string): string {
    switch (alerticon) {
      case 'MidRange':
        return 'Claimant has reached the middle of the recommended return to work date range.  Please review the ICD tab within the Medical Detail screen for RTW best practices. WFA  said they did not need the RTW best practices info on the ICD screen';
      case 'At-risk':
        return 'Claimant is within 7 days of the recommended duration for the specific injury/illness.  Please review the Medical screen for more information';
      case 'Triage':
        return 'Nurse triage was performed and triage notes exist for the claim';
      case 'Alert':
        return (
          (this.alertTextEvent ? 'Event: ' + this.alertTextEvent + '\n' : '') +
          (this.alertTextClaim ? 'Claim: ' + this.alertTextClaim + '\n' : '') +
          (this.alertTextClaimant ? 'Claimant: ' + this.alertTextClaimant : '')
        );
      default:
        return '';
    }
  }

  @HandleLoadingState({
    loadingProperty: 'loadingState',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  async ngOnInit(): Promise<void> {
    this.restDsLaAuth = this.view.authorize('RestDsLa');
    this.custDispAuth = this.view.authorize('CustDisp');
    this.disabilityAuth = this.session.authorize('Disability');
    this.claimRow = this.view.currentClaim();

    const visibility = getFieldVisibility(
      this.session.user?.fieldSecurity?.restrictedFields,
    );
    this.showSubType = visibility.showSubType;
    this.showDateOpened = visibility.showDateOpened;
    this.showDateClosed = visibility.showDateClosed;
    this.showDateRptByClient = visibility.showDateRptByClient;
    this.showDateReptToTPA = visibility.showDateReptToTPA;
    this.showClaimDescription = visibility.showClaimDescription;
    this.showBenefitPlanId =
      this.session.authorize('BenefitPlan') &&
      this.session.authorize('BenefitSummary');

    if (this.claimRow.lineOfBusiness === 'DS') {
      this.claimSummaryInfo = await this.api.claimSummary.getClaimSummaryInfo(
        this.claimRow.recordSource,
        this.claimRow.recordId,
      );
      this.alerticons = await this.api.alert.getAlertIcon(
        this.claimRow.recordSource,
        this.claimRow.recordId,
      );
      if (this?.alerticons) {
        this.litigation = !!this.alerticons.litigation;
        this.relapse = !!this.alerticons.relapse;
        this.subro = !!this.alerticons.subro;
        this.appeal = !!this.alerticons.appeal;
        this.midRange = !!this.alerticons.midRange;
        this.atRisk = !!this.alerticons.atRisk;
        this.subOwner = !!this.alerticons.subOwner;
        this.overpayBalance = !!this.alerticons.overpayBalance;
        this.overpayRecovered = !!this.alerticons.overpayRecovered;
        this.tendered = !!this.alerticons.tendered;
        this.nurseTriage = !!this.alerticons.nurseTriage;
        this.alertTextEvent = this.alerticons.alertTextEvent || '';
        this.alertTextClaim = this.alerticons.alertTextClaim || '';
        this.alertTextClaimant = this.alerticons.alertTextClaimant || '';
        this.claimantRepresented = this.alerticons.claimantRepresented;
        this.workrelated = this.alerticons.workRelated;
      }
    }
  }
}
